import React, {
  ReactElement,
  Children,
  cloneElement,
  FC,
  isValidElement,
  RefObject,
  useEffect,
  useState,
  CSSProperties,
  useMemo,
  useCallback
} from 'react';
import useDeepCompareEffectForMaps from '../../hooks/useDeepCompareEffectForMaps';
import useGoogleMaps from '../../providers/GoogleMaps/GoogleMaps.provider';
import ReactDOM from 'react-dom/client';
import { AmplitudeEvent, logAmplitudeEventWithClassName } from '../../utils/amplitudeUtils';

interface TwMapProps extends google.maps.MapOptions {
  map?: google.maps.Map;
  setMap: React.Dispatch<React.SetStateAction<google.maps.Map | undefined>>;
  mapRef: RefObject<HTMLDivElement>;
  className?: string;
  style?: CSSProperties;
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onIdle?: (map: google.maps.Map) => void;
  children?: React.ReactNode;
  showReloadButton?: boolean;
  onReload?: () => Promise<void>;
}

interface MapChildProps {
  map?: google.maps.Map;
}

/*
* --- Usage --- *
- Always pass the center prop in order to avoid rendering issues.

- Need to declare a map state and ref in parent component: 
Example:
const [map, setMap] = useState<google.maps.Map>()
const mapRef = useRef<HTMLDivElement | null>(null)

Notes:
- If you are using this component inside a modal / drawer or similar components
you need to destroy the map and mapRef when components "closes"
Example:
const onClose = () => {
  setMap(undefined);
  mapRef.current = null;      
};
*/

const TwMap: FC<TwMapProps> = ({
  map,
  setMap,
  mapRef,
  children,
  className,
  style,
  onClick,
  onIdle,
  showReloadButton,
  onReload,
  ...options
}) => {
  const { loaded } = useGoogleMaps();
  const [zoom, setZoom] = useState(4);
  const [isReloading, setIsReloading] = useState(false);

  // Memoize the map options to prevent unnecessary changes
  const mapOptions = useMemo(
    () => ({
      zoom,
      zoomControl: true,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      ...options
    }),
    [zoom, JSON.stringify(options)]
  );

  // Memoize the map creation function
  const createMap = useCallback(() => {
    if (!mapRef.current || !loaded) return null;

    logAmplitudeEventWithClassName(AmplitudeEvent.GOOGLE_MAPS_REQUEST, className || 'undefined');
    console.debug('Creating new Google Map');
    const newMap = new window.google.maps.Map(mapRef.current, mapOptions);

    // Handle zoom changes
    newMap.addListener('zoom_changed', () => {
      setZoom(newMap.getZoom()!);
    });

    return newMap;
  }, [mapRef, loaded, mapOptions]);

  useEffect(() => {
    if (mapRef.current && !map && loaded) {
      const newMap = createMap();
      if (newMap) {
        setMap(newMap);
      }
    }
  }, [mapRef, map, loaded, createMap]);

  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options);
    }
  }, [map, options]);

  // Cursor AI geenerated more react solution to show spinner loading icon spinning
  const ReloadControl = (): JSX.Element => {
    const handleReload = async (): Promise<void> => {
      if (isReloading || !onReload) return;

      setIsReloading(true);
      try {
        await onReload();
        await new Promise((resolve) => setTimeout(resolve, 500));
      } finally {
        setIsReloading(false);
      }
    };

    return (
      <button
        onClick={handleReload}
        disabled={isReloading}
        style={{
          backgroundColor: '#fff',
          border: '2px solid #fff',
          borderRadius: '3px',
          boxShadow: '0 2px 6px rgba(0,0,0,.3)',
          cursor: isReloading ? 'not-allowed' : 'pointer',
          margin: '8px 8px 22px',
          padding: '0 5px',
          height: '40px',
          width: '40px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          opacity: isReloading ? 0.7 : 1
        }}
      >
        <div
          className={isReloading ? 'reload-loading' : ''}
          style={{
            backgroundImage: `url(${window.origin}/reload.png)`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            width: '20px',
            height: '20px'
          }}
        />
      </button>
    );
  };

  useEffect(() => {
    if (map) {
      ['click', 'idle'].forEach((eventName) => google.maps.event.clearListeners(map, eventName));

      if (onClick) {
        map.addListener('click', onClick);
      }

      if (onIdle) {
        map.addListener('idle', () => onIdle(map));
      }

      map.controls[google.maps.ControlPosition.TOP_RIGHT].clear();
      if (showReloadButton && onReload) {
        const controlDiv = document.createElement('div');
        const root = ReactDOM.createRoot(controlDiv);
        root.render(<ReloadControl />);
        map.controls[google.maps.ControlPosition.TOP_RIGHT].push(controlDiv);
      }
    }
  }, [map, onClick, onIdle, showReloadButton, onReload, isReloading]);

  const mergedStyle: CSSProperties = {
    ...style,
    overflow: 'visible',
    position: 'relative',
    width: '100%'
  };

  return (
    <>
      <div ref={mapRef} className={className} style={mergedStyle} />
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          // Set the map prop on the child component.
          return cloneElement(child as ReactElement<MapChildProps>, { map });
        }
        return null;
      })}
    </>
  );
};

export default TwMap;
